import { cva, type VariantProps } from 'class-variance-authority'
import clsx from 'clsx'
import { twMerge } from 'tailwind-merge'
import React from 'react'
import { Label } from '@strata-monorepo/shared-ui/components/ui/label'

export interface ToggleProps {
  label?: string
  options: { label: string; value: string; subtext?: string }[]
  values: string[]
  onChange: (values: string[]) => void
  multiSelect?: boolean
  className?: string
  optionClassName?: string
  'aria-label'?: string
  required?: boolean
}

const baseVariants = cva('strata:font-semibold', {
  variants: {
    variant: {
      filled: 'strata:bg-secondary-50 strata:rounded-[8px]',
      outlined: 'strata:bg-white strata:rounded-none',
    },
    size: {
      sm: 'strata:text-sm',
      md: 'strata:text-md',
    },
  },
})

const optionVariants = cva(
  'strata:flex strata:flex-col strata:items-center strata:py-2 strata:px-4 strata:justify-center strata:flex-1 strata:cursor-pointer strata:focus-visible:z-99',
  {
    variants: {
      variant: {
        filled: 'strata:rounded-secondary',
        outlined:
          'strata:border strata:not-first:border-l-0 strata:border-secondary-500 strata:last:rounded-tr-secondary strata:last:rounded-br-secondary strata:first:rounded-tl-secondary strata:first:rounded-bl-secondary',
      },
      state: {
        default: '',
        selected: 'strata:text-white',
      },
    },
    compoundVariants: [
      {
        variant: 'outlined',
        state: 'default',
        className: 'strata:bg-white strata:hover:bg-secondary-500 strata:hover:text-white',
      },
      {
        variant: 'outlined',
        state: 'selected',
        className: 'strata:bg-secondary-500',
      },
      {
        variant: 'filled',
        state: 'default',
        className: 'strata:bg-secondary-50',
      },
      {
        variant: 'filled',
        state: 'selected',
        className: 'strata:bg-secondary-500 strata:m-0.5',
      },
    ],
  },
)

type BaseVariantProps = VariantProps<typeof baseVariants>
type OptionVariantProps = VariantProps<typeof optionVariants>

export function Toggle({
  label,
  options,
  values,
  onChange,
  className,
  optionClassName,
  'aria-label': ariaLabel,
  multiSelect,
  variant,
  size,
  required = false,
}: ToggleProps & BaseVariantProps): React.ReactElement {
  const handleChange = (id: string): void => {
    if (multiSelect) {
      // For multi-select, toggle the selected value
      const newValues = values.includes(id)
        ? values.filter((v) => v !== id) // Remove if already selected
        : [...values, id] // Add if not already selected
      onChange(newValues)
    } else {
      // For single-select, just replace the value
      onChange([id])
    }
  }

  return (
    <div
      data-testid='toggle'
      aria-label={ariaLabel}
      className={className}
      role={multiSelect ? 'group' : 'radiogroup'}
      {...(label ? { 'aria-labelledby': 'toggle-label' } : {})}
    >
      {label ? (
        <Label
          className='strata:text-gray-600 strata:text-xs strata:flex strata:pb-0.5 strata:@qm/qc:pb-1'
          id='toggle-label'
        >
          {label}
          {required ? (
            <span className='strata:ml-0.5 strata:text-red-500 strata:text-xs strata:leading-4'>
              *
            </span>
          ) : null}
        </Label>
      ) : null}

      <div
        className={clsx(
          baseVariants({ variant, size }),
          'strata:flex strata:text-secondary-500 strata:flex-row strata:w-full',
        )}
      >
        {options.map((option) => {
          return (
            <ToggleOption
              key={option.value}
              option={option}
              selected={values.includes(option.value)}
              onChange={handleChange}
              variant={variant}
              multiSelect={multiSelect}
              optionClassName={optionClassName}
            />
          )
        })}
      </div>
    </div>
  )
}

type ToggleOptionProps = {
  option: { label: string; value: string; subtext?: string }
  selected?: boolean
  onChange: (value: string) => void
  multiSelect?: boolean
  optionClassName?: string
} & OptionVariantProps

function ToggleOption({
  option,
  selected,
  onChange,
  variant,
  multiSelect,
  optionClassName,
}: ToggleOptionProps): React.ReactElement {
  return (
    <button
      className={twMerge(
        clsx(optionVariants({ variant, state: selected ? 'selected' : 'default' })),
        optionClassName,
      )}
      key={option.value}
      onClick={() => onChange(option.value)}
      {...(multiSelect
        ? { 'aria-pressed': selected } // For toggle buttons
        : { 'aria-checked': selected })} // For radio buttons
      role={multiSelect ? 'checkbox' : 'radio'}
      {...(option.subtext ? { 'aria-describedby': 'toggle-subtext' } : {})}
    >
      {option.label}
      {option.subtext && (
        <span
          id='toggle-subtext'
          className={clsx('strata:text-[10px] strata:font-normal', {
            'strata:text-white': selected,
          })}
        >
          {option.subtext}
        </span>
      )}
    </button>
  )
}
